/**
 * Smooth camera transition system.
 *
 * Call `startCameraTransition()` to kick off an animated move from the current
 * camera to a destination. Mount `<CameraTransitionDriver>` inside the R3F
 * Canvas to tick the animation each frame via `useFrame`.
 */

import { invalidate as r3fInvalidate, useFrame } from "@react-three/fiber";
import * as THREE from "three/webgpu";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useStore } from "../app/store.ts";
import { recordInvalidate } from "../debug/listenerProbe.ts";
import { flushCameraSync } from "./cameraSync.ts";
import { viewLockBreakReason } from "./viewLock.ts";

const DURATION = 0.5; // seconds

/** Ease-out cubic: fast start, smooth deceleration. */
function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

// ---------------------------------------------------------------------------
// Module-level singleton (single-instance app)
// ---------------------------------------------------------------------------

interface Transition {
  active: boolean;
  fromPos: THREE.Vector3;
  fromTarget: THREE.Vector3;
  fromFocalLength: number;
  toPos: THREE.Vector3;
  toTarget: THREE.Vector3;
  toFocalLength: number;
  elapsed: number;
  duration: number;
  /**
   * When non-null the transition also slerps the camera quaternion from its
   * starting orientation to a destination computed by looking at `toTarget`
   * from `toPos` with `toUp` as the camera-up vector. This is essential for
   * axis-view switches that change the up axis (e.g. Front → Top): without
   * quaternion interpolation the first `controls.update()` after setting the
   * new up vector would snap the camera's orientation instantly.
   */
  toUp: THREE.Vector3 | null;
  fromQuat: THREE.Quaternion;
  toQuat: THREE.Quaternion;
}

const tx: Transition = {
  active: false,
  fromPos: new THREE.Vector3(),
  fromTarget: new THREE.Vector3(),
  fromFocalLength: 50,
  toPos: new THREE.Vector3(),
  toTarget: new THREE.Vector3(),
  toFocalLength: 50,
  elapsed: 0,
  duration: DURATION,
  toUp: null,
  fromQuat: new THREE.Quaternion(),
  toQuat: new THREE.Quaternion(),
};

// Scratch vectors to avoid per-frame allocations.
const _pos = new THREE.Vector3();
const _tgt = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _destHelper = new THREE.Object3D();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Whether a camera transition is currently in progress. */
export function isCameraTransitioning(): boolean {
  return tx.active;
}

/**
 * Begin a smooth transition from the current camera to the given destination.
 * If a transition is already running it is replaced.
 *
 * If `toUp` is provided, the camera's quaternion is slerped from its current
 * orientation to the one implied by looking at `toTarget` from `toPos` with
 * that up vector — use this for view-axis switches that rotate the up axis.
 * The live camera's `.up` is NOT modified until the transition completes;
 * the caller is responsible for applying it at that point (or allowing the
 * driver to, via the store's `cameraUp`).
 *
 * The `from*` snapshot is deferred to the first frame of the animation — the
 * driver reads position / target / quaternion directly from the live camera
 * and controls. Reading from the store at call-time would snapshot stale
 * values whenever a previous transition is still in flight (the store is
 * only written on orbit-end + at transition completion), producing pops or
 * wrong-direction slerps on rapid retriggers.
 */
export function startCameraTransition(
  toPos: [number, number, number],
  toTarget: [number, number, number],
  toFocalLength: number,
  toUp: [number, number, number] | null = null,
  duration: number = DURATION,
): void {
  const state = useStore.getState();
  const breakReason = viewLockBreakReason(state.viewLock, toPos, toTarget);
  if (breakReason) {
    const lock = state.viewLock;
    if (lock) {
      window.__poseref_cameraController?.applyState({
        projection: lock.prevProjection,
        up: lock.prevProjection === "perspective" ? null : state.cameraUp,
        focalLength: lock.prevFocalLength,
        orthographicHalfHeight:
          lock.prevOrthographicHalfHeight ?? state.orthographicHalfHeight,
      });
    }
    state.exitViewLock(breakReason);
  }

  tx.toPos.set(...toPos);
  tx.toTarget.set(...toTarget);
  tx.toFocalLength = toFocalLength;
  tx.elapsed = 0;
  tx.duration = duration;

  if (toUp) {
    tx.toUp = (tx.toUp ?? new THREE.Vector3()).set(...toUp);
    // Compute destination quaternion: a camera at toPos looking at toTarget
    // with the given up vector. We use a throwaway Object3D since its
    // lookAt implementation matches a Camera's frame convention.
    _destHelper.up.set(...toUp);
    _destHelper.position.set(...toPos);
    _destHelper.lookAt(tx.toTarget);
    tx.toQuat.copy(_destHelper.quaternion);
  } else {
    tx.toUp = null;
  }
  // fromQuat is captured on first tick regardless, so reset to identity
  // to signal "not yet captured".
  tx.fromQuat.identity();

  tx.active = true;
  r3fInvalidate(); // seed the first frame in demand mode
}

// ---------------------------------------------------------------------------
// R3F driver component
// ---------------------------------------------------------------------------

/**
 * Drives the camera transition each frame. Mount once inside the Canvas.
 */
export function CameraTransitionDriver({
  controlsRef,
}: {
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
}) {
  useFrame((state, delta) => {
    if (!tx.active) return;
    const c = controlsRef.current;
    if (!c) return;

    // On the first frame, capture the starting camera state from the LIVE
    // camera / controls rather than from the store. The store is only
    // written at orbit-end and at transition completion, so a retrigger
    // mid-animation would otherwise snapshot stale values (pops, wrong-
    // direction slerps). Capturing here also neatly subsumes the case
    // where React's camera-projection swap hasn't run yet by the time
    // startCameraTransition was called.
    if (tx.elapsed === 0) {
      tx.fromPos.copy(c.object.position);
      tx.fromTarget.copy(c.target);
      // FOV → focal length would be cleaner but we only need a scalar that
      // matches what setFocalLength will restore on completion; in practice
      // focalLength on the store tracks the live persp camera outside
      // transitions, and transitions preserve it for axis-view snaps.
      tx.fromFocalLength = useStore.getState().focalLength;
      if (tx.toUp) {
        tx.fromQuat.copy(c.object.quaternion);
      }
    }

    tx.elapsed += delta;
    const t = tx.duration <= 0 ? 1 : Math.min(tx.elapsed / tx.duration, 1);
    const e = easeOutCubic(t);

    _pos.lerpVectors(tx.fromPos, tx.toPos, e);
    _tgt.lerpVectors(tx.fromTarget, tx.toTarget, e);

    c.object.position.copy(_pos);
    c.target.copy(_tgt);

    if (tx.toUp) {
      // Orientation is driven manually via slerp; skip OrbitControls' update
      // because it would call lookAt() with the camera's current (stale)
      // up vector and clobber the slerped quaternion. We apply the final up
      // + a single update() at completion below to resync OrbitControls'
      // internal spherical state.
      _quat.slerpQuaternions(tx.fromQuat, tx.toQuat, e);
      c.object.quaternion.copy(_quat);
      c.object.updateMatrixWorld();
    } else {
      c.update();
    }

    if (t >= 1) {
      tx.active = false;
      // Re-enable orbit controls now that the transition is done.
      const s = useStore.getState();
      const effectiveOrbit = s.orbitEnabled !== s.spaceOrbitActive;
      c.enabled =
        s.commandBuffer == null &&
        (effectiveOrbit || s.workspaceMode === "render");
      // Apply the final up vector and let OrbitControls rebuild its
      // internal spherical state from the now-correct position/target/up.
      if (tx.toUp) {
        c.object.up.copy(tx.toUp);
        c.update();
      }
      // Sync final focal length and camera position to the store. The
      // coordinator's atomic write folds cameraUp into the same setState
      // as position/target, eliminating the two-step desync window that
      // used to exist between an isolated `{cameraUp}` write and the
      // subsequent position/target sync.
      s.setFocalLength(tx.toFocalLength);
      flushCameraSync();
    } else {
      recordInvalidate("cameraTransition");
      state.invalidate(); // sustain the animation until t reaches 1
    }
  });

  return null;
}
